import React, { useEffect, useRef, useState } from 'react';
import Peer from 'peerjs';
import { callAPIForQuery } from '../../../utilities/service';
import { Toaster, toast } from 'react-hot-toast';
import { TOAST } from '../../../utilities/constant';
import { ToastPanel } from '../../common/ToastPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/free-solid-svg-icons';

type RemoteStreamWithInfo = {
    stream: MediaStream;
    label: string;
    userName: string;
    peerIdValue: string;
};

type ChatMessage = {
    user: string;
    message: string;
};

const WebRTCComponentWithChatAndVideo = ({ onTrackIDGenerate, destinationId, sourceId, loggedInUser }: any) => {
    const [peerId, setPeerId] = useState('');
    const [remotePeerIdValue, setRemotePeerIdValue] = useState('');
    const [remoteStreams, setRemoteStreams] = useState<RemoteStreamWithInfo[]>([]);
    const [localCameraStreams, setLocalCameraStreams] = useState<MediaStream[]>([]);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatInput, setChatInput] = useState('');
    const localCameraStreamsRef = useRef<MediaStream[]>([]);
    const peerInstance = useRef<Peer | null>(null);
    const connRef = useRef<any>(null);

    useEffect(() => {
        const setupPeer = async () => {
            const peer = new Peer({
                host: 'mgmvc1.mgmhealthcare.in',
                port: 8443,
                path: '/myapp',
                secure: true,
            });

            peer.on('open', (id) => {
                onTrackIDGenerate(id);
                setPeerId(id);
            });

            peer.on('connection', (conn) => {
                connRef.current = conn;
                conn.on('data', (data: any) => {
                    setChatMessages((prev) => [...prev, { user: conn.peer, message: data }]);
                });
            });

            // Get camera streams
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoInputs: any = devices.filter((device) => device.kind === 'videoinput');
            const streams: MediaStream[] = [];

            for (const [i, device] of videoInputs.entries()) {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                        video: { deviceId: { ideal: device.deviceId } },
                        audio: i === 0,
                    });
                    streams.push(stream);
                } catch (error) {
                    console.error(`Error accessing camera ${i}:`, error);
                }
            }

            setLocalCameraStreams(streams);
            localCameraStreamsRef.current = streams;

            peer.on('call', (call) => {
                const index = parseInt(call.metadata?.cameraIndex ?? '0');
                const streamToAnswer = localCameraStreamsRef.current[index];

                if (streamToAnswer) {
                    call.answer(streamToAnswer);
                }

                call.on('stream', async (remoteStream) => {
                    const payload = `{"tracking_id": "${call.peer}"}`;
                    const result: any = await callAPIForQuery({ input: payload, type: 'get_his_track' }, 'post');
                    const userName = result?.[0]?.[0]?.created_by || 'NA';
                    const label = call.metadata?.sourceId || `Remote Camera ${call.metadata?.cameraIndex ?? ''}`;

                    remoteStream.getTracks().forEach((track) => {
                        track.onended = () => {
                            if (loggedInUser !== userName) {
                                toast.error(<ToastPanel message={`Stream from ${userName} has ended. Hence re-join`} />, TOAST)
                            }
                            setRemoteStreams((prev) => prev.filter((s) => s.peerIdValue !== call.peer));
                        };
                    });

                    setRemoteStreams((prev) => {
                        if (!prev.find((s) => s.stream.id === remoteStream.id)) {
                            return [...prev, { stream: remoteStream, label, userName, peerIdValue: call.peer }];
                        }
                        return prev;
                    });
                });

                call.on('close', () => {
                    setRemoteStreams((prev) => prev.filter((s) => s.peerIdValue !== call.peer));
                });

                call.on('error', (err) => {
                    console.error('Call error:', err);
                });
            });

            peerInstance.current = peer;
        };

        setupPeer();

        return () => {
            peerInstance.current?.destroy();
            localCameraStreamsRef.current.forEach((stream) => {
                stream.getTracks().forEach((track) => track.stop());
            });
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (destinationId) {
            setRemotePeerIdValue(destinationId);
            callRemotePeer(destinationId);
        }
    }, [destinationId]); // eslint-disable-line react-hooks/exhaustive-deps

    const callRemotePeer = async (remotePeerId: string) => {
        if (!peerInstance.current || localCameraStreams.length === 0) return;
        for (let i = 0; i < 5; i++) {
            const stream = localCameraStreams[i] || localCameraStreams[0];;
            const videoLabel = stream.getVideoTracks()[0]?.label || `Camera ${i}`;
            const call = peerInstance.current.call(remotePeerId, stream, {
                metadata: {
                    cameraIndex: i,
                    deviceId: videoLabel,
                    sourceId,
                    userName: loggedInUser,
                },
            });

            call.on('stream', async (remoteStream: MediaStream) => {
                const payload = `{"tracking_id": "${call.peer}"}`;
                const result: any = await callAPIForQuery({ input: payload, type: 'get_his_track' }, 'post');
                const userName = result?.[0]?.[0]?.created_by || 'NA';
                const label = call.metadata?.sourceId || `Remote Camera ${call.metadata?.cameraIndex ?? ''}`;

                remoteStream.getTracks().forEach((track) => {
                    track.onended = () => {
                        if (loggedInUser !== userName) {

                            toast.error(<ToastPanel message={`Stream from ${userName} ended. Hence re-join`} />, TOAST)

                        }
                        setRemoteStreams((prev) => prev.filter((s) => s.peerIdValue !== call.peer));
                    };
                });

                setRemoteStreams((prev) => {
                    if (!prev.find((s) => s.stream.id === remoteStream.id)) {
                        return [...prev, { stream: remoteStream, label, userName, peerIdValue: call.peer }];
                    }
                    return prev;
                });
            });

            call.on('error', (err) => {
                console.error('Call error:', err);
            });
        }
        await new Promise(res => setTimeout(res, 2000)); // delay 2s
        connRef.current = peerInstance.current.connect(remotePeerId);
        connRef.current.on('data', (data: any) => {
            setChatMessages((prev) => [...prev, { user: remotePeerId, message: data }]);
        });
    };

    const sendMessage = () => {
        if (chatInput && connRef.current?.open) {
            connRef.current.send(chatInput);
            setChatMessages((prev) => [...prev, { user: 'Me', message: chatInput }]);
            setChatInput('');
        }
    };
    const remoteVideoList = remoteStreams
        .filter(({ label }) => !(label === sourceId && sourceId === destinationId))

    let peerObj: any = {}
    return (
        <div className="App">
            <Toaster position="bottom-right" />

            <div className="stream-section" style={{ display: "none" }} >
                <div className="header-row">Local Camera:</div>
                <div className="video-section" style={{ display: 'flex', gap: '10px' }}>
                    {localCameraStreams.map((stream, index) => (
                        <div key={`local-${index}`}>
                            <video
                                autoPlay
                                playsInline
                                muted
                                ref={(videoEl) => {
                                    if (videoEl) {
                                        videoEl.srcObject = stream;
                                    }
                                }}
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className="stream-section">
                <div className="video-section" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {
                        remoteVideoList.map(({ stream, userName, peerIdValue }, index) => {
                            if (!peerObj[peerIdValue]) {
                                peerObj[peerIdValue] = 0;
                            }
                            peerObj[peerIdValue] = peerObj[peerIdValue] + 1;
                            return <div key={`remote-${index}`}>
                                <video
                                    autoPlay
                                    playsInline
                                    data-peer-id={peerIdValue}
                                    data-stream-id={stream.id}
                                    ref={(videoEl) => {
                                        if (videoEl) {
                                            videoEl.srcObject = stream;
                                        }
                                    }}
                                />
                                <p style={{ textTransform: 'capitalize', textAlign: 'center', fontSize: '0.85rem' }}>
                                    {userName} @Camera-{peerObj[peerIdValue]}
                                </p>
                            </div>
                        }


                        )}
                </div>
            </div>

            {remoteVideoList.length > 0 && (<div className="chat-section" style={{ marginTop: '20px', padding: '10px' }}>
               <h4> <FontAwesomeIcon  icon={faComment}  /> Chat</h4>
                {chatMessages.length > 0 && (<div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '10px', border: '1px solid #ddd', padding: '8px' }}>
                    {chatMessages.map((msg, idx) => {
                        let userName = msg.user;
                        if (msg.user !== "Me") {
                            const selectedObj = remoteVideoList.find((obj: any) => obj.peerIdValue === msg.user);
                            if (selectedObj) userName = selectedObj.userName
                        }

                        return (
                            <div key={idx}>
                                <strong>{userName}: </strong>{msg.message}
                            </div>
                        )
                    })}
                </div>)}
                <input
                    type="text"
                    className='form-control'
                    value={chatInput}
                    onKeyUp={(e: any) => {
                        if (e.key === "Enter") {
                           // setChatInput(e.target.value)
                            sendMessage();
                        }
                    }}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Type your message..."
                    style={{ width: '80%', marginRight: '10px' }}
                />
                <br />
                <input onClick={sendMessage} type="button" value="Send" />
                {/* <button onClick={sendMessage}>Send</button> */}
            </div>)}
        </div>
    );
};

export default WebRTCComponentWithChatAndVideo;
